import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { animeListImages } from '../../services/images.service'
import { getAnimeDetailsUrl, getAnimeImage } from '../../services/util.service'
import { AnimeWatchlistEdit } from '../UserProfile/UserWatchlist/AnimeWatchlistEdit'
import { ReviewPopup } from '../ReviewSystem/ReviewPopup'
import { useWatchlistActions } from '../../hooks/useWatchlistActions'
import { EditPen } from '../ui/icons/user'
import { MinusIcon, PlusIcon } from '../ui/icons/shared'

export function AnimePreview({ anime }) {
  /* ─────────── Redux state ─────────── */
  const loggedInUser = useSelector((s) => s.userModule.loggedInUser)
  const userId = loggedInUser?._id
  const statusMap = useSelector((s) => s.watchlist.statusMap[userId] || {}) // נטען רק ל־IDs שמופיעים בדף
  const reviews = useSelector((s) => s.reviewModule.reviews) || []

  /* ─────────── Watchlist item (אם קיים) ─────────── */
  const existingItem = statusMap[anime.mal_id]
    ? { anizone_id: anime.mal_id, ...statusMap[anime.mal_id] }
    : null

  /* ─────────── UI state ─────────── */
  const [isLiHovered, setIsLiHovered] = useState(false)
  const [isPlusHovered, setIsPlusHovered] = useState(false)
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [editData, setEditData] = useState({})
  const [reviewPopupOpen, setReviewPopupOpen] = useState(false)
  const [initialReviewData, setInitialReviewData] = useState(null)

  /* ─────────── Watchlist actions ─────────── */
  const {
    handleAddItem,
    handleSaveChanges,
    handleRemoveItem,
    getUpdatedWatchItem,
  } = useWatchlistActions(userId)

  /* ─────────── Helpers ─────────── */
  const userAlreadyReviewed = useMemo(
    () => (animeId) =>
      reviews.find((r) => r.animeId === animeId && r.userId === userId),
    [reviews, userId]
  )

  const handleOpenReviewPopup = (animeId) => {
    const review = userAlreadyReviewed(animeId)
    setInitialReviewData(review || null)
    setReviewPopupOpen(true)
  }

  const titleHebrew = anime.title_hebrew || ''
  const titleEnglish = anime.title_english || anime.title || ''
  const altTitle = `${titleHebrew} // ${titleEnglish}`
  const imgAlt =
    titleHebrew && titleEnglish
      ? `${titleEnglish} (${titleHebrew}) poster`
      : `${titleEnglish || titleHebrew} poster`

  /* ─────────── JSX ─────────── */
  return (
    <>
      <li
        className="flex flex-col items-center relative transition-transform duration-300 hover:scale-105"
        onMouseEnter={() => setIsLiHovered(true)}
        onMouseLeave={() => {
          setIsLiHovered(false)
          setIsPlusHovered(false)
        }}
        itemScope
        itemType="https://schema.org/TVSeries"
      >
        <meta itemProp="name" content={titleEnglish || titleHebrew} />
        <div className="relative w-full h-[320px]">
          <Link
            to={getAnimeDetailsUrl(anime)}
            className="block w-full h-full"
            title={altTitle}
            aria-label={`לפרטי אנימה: ${altTitle}`}
            itemProp="url"
          >
            <img
              src={getAnimeImage(anime)}
              alt={imgAlt}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover transition-transform duration-300"
              itemProp="image"
            />
          </Link>

          {existingItem && isLiHovered ? (
            <button
              onClick={() => {
                setEditData({
                  progress: existingItem.progress,
                  status: existingItem.status,
                  start_date: existingItem.start_date,
                  finish_date: existingItem.finish_date,
                })
                setIsEditOpen(true)
              }}
              title="ערוך סטטוס צפייה"
              aria-label="ערוך סטטוס צפייה"
              className="absolute bottom-16 left-2 z-20 w-10 h-10 bg-accent rounded-full flex items-center justify-center hover:scale-110 transition-transform duration-300"
            >
              <EditPen />
            </button>
          ) : (
            !existingItem && (
              <div
                className={`absolute bottom-16 left-2 z-20 flex gap-2 transition-opacity duration-300 ${
                  isLiHovered ? 'opacity-100 scale-105' : 'opacity-0 scale-95'
                }`}
                onMouseEnter={() => setIsPlusHovered(true)}
                onMouseLeave={() => setIsPlusHovered(false)}
              >
                <button
                  className="w-10 h-10 bg-accent rounded-full flex items-center justify-center transition-transform duration-300 hover:scale-110"
                  title="הוסף ל־Watching"
                  aria-label="הוסף ל־Watching"
                  onClick={() => handleAddItem('Watching', anime)}
                >
                  {isPlusHovered ? <MinusIcon /> : <PlusIcon />}
                </button>
                <div
                  className={`absolute bottom-10 left-1/2 transform -translate-x-1/2 p-2 rounded-lg flex flex-col gap-1 transition-all duration-300 ${
                    isPlusHovered
                      ? 'opacity-100 scale-105'
                      : 'opacity-0 scale-95'
                  }`}
                >
                  <button
                    title="הוסף לרשימת צפייה"
                    aria-label="הוסף ל־Watching"
                    className="w-10 h-10 bg-accent rounded-full flex items-center justify-center transition-transform duration-300 hover:scale-110"
                    onClick={() => handleAddItem('Watching', anime)}
                  >
                    <img
                      src={animeListImages.addToWatch}
                      alt="אייקון הוספה לצפייה"
                      className="w-4 h-4"
                    />
                  </button>
                  <button
                    title="הוסף ל־Plan to Watch"
                    aria-label="הוסף ל־Plan to Watch"
                    className="w-10 h-10 bg-accent rounded-full flex items-center justify-center transition-transform duration-300 hover:scale-110"
                    onClick={() => handleAddItem('Plan to Watch', anime)}
                  >
                    <img
                      src={animeListImages.planToWatch}
                      alt="אייקון תכנון צפייה"
                      className="w-4 h-4"
                    />
                  </button>
                </div>
              </div>
            )
          )}

          <Link
            to={getAnimeDetailsUrl(anime)}
            className="absolute bottom-0 left-0 w-full z-10"
            title={altTitle}
            aria-label={`כותרת: ${altTitle}`}
          >
            <div className="bg-secondaryBackground bg-opacity-75 text-center p-2">
              <p
                className="text-white rtl-truncate max-w-full overflow-hidden whitespace-nowrap text-ellipsis"
                title={altTitle}
              >
                <span lang="he" itemProp="alternateName">
                  {titleHebrew}
                </span>{' '}
                //{' '}
                <span lang="en" itemProp="name">
                  {titleEnglish}
                </span>
              </p>
            </div>
          </Link>
        </div>
      </li>

      {isEditOpen && existingItem && (
        <AnimeWatchlistEdit
          item={existingItem}
          editData={editData}
          setEditData={setEditData}
          handleCloseEdit={() => setIsEditOpen(false)}
          handleSaveItemChanges={handleSaveChanges}
          getUpdatedWatchItem={getUpdatedWatchItem}
          handleOpenReviewPopup={handleOpenReviewPopup}
          userAlreadyReviewed={userAlreadyReviewed}
          onRemove={() => {
            handleRemoveItem(existingItem.anizone_id)
            setIsEditOpen(false)
          }}
        />
      )}

      {reviewPopupOpen && existingItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <ReviewPopup
            animeId={existingItem.anizone_id}
            initialData={initialReviewData}
            loggedInUser={loggedInUser}
            onClose={() => setReviewPopupOpen(false)}
          />
        </div>
      )}
    </>
  )
}
